import { useEffect, useState, useRef, useCallback } from "react"; 
import { useLocation } from "react-router-dom";
import useGetManyPOs from "../../hooks/pos/useGetManyPOs";
import { CREATE_PO, GENERATE_PO_PDF } from "../../graphql/mutations/pos/pos.mutations";
import { useMutation } from "@apollo/client";

const ManualStatementPage = () => {
  const location = useLocation();
  const customer = location.state?.customer || {};

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [email, setEmail] = useState("");
  
  const { purchaseOrders, setCustomerId, getPOs } = useGetManyPOs();
  const customerIdRef = useRef(null);

  const [createPO] = useMutation(CREATE_PO);
  const [generatePOPDF] = useMutation(GENERATE_PO_PDF);

  const handleCreatePO = async () => {
    if (!customer?.id) {
      alert("Customer ID is missing");
      return;
    }

    try {
      const { data } = await createPO({
        variables: {
          customer_id: parseInt(customer.id, 10),
          start_date: fromDate,
          end_date: toDate,
          email,
        },
      });

      if (data?.createPO._id) {
        setCustomerId(customer.id);
        getPOs();
        alert("Purchase Order Created Successfully");
      }else{
        alert("Purchase Order Failed");
      }
    } catch (error) {
      alert(`Error creating PO: ${error.message}`);
    }
  };

  const fetchPOs = useCallback(() => {
    if (!customer?.id || customerIdRef.current === customer.id) return;
    
    console.log("Fetching POs for customer:", customer.id);
    customerIdRef.current = customer.id;
    setCustomerId(customer.id);
    getPOs();
  }, [customer?.id]);

  useEffect(() => {
    fetchPOs();
  }, [fetchPOs]);

  const handleDownloadPO = async (poId) => {
    try {
      const { data } = await generatePOPDF({
        variables: { _id: poId },
      });

      if (data?.purchaseOrderPDF) {
        const base64Data = data.purchaseOrderPDF;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        // Create a download link
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `PO_${poId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      alert(`Error downloading PO PDF: ${error.message}`);
    }
  };

  return (
    <div className="p-6 flex flex-col gap-6 items-center bg-gray-100 min-h-screen">
      <h1 className="text-2xl font-bold text-gray-800">{customer.business_name || "Customer"}</h1>

      <div className="bg-white shadow-md rounded-lg p-4 w-full max-w-6xl">
        <div className="flex gap-4 mb-4">
          <input
            type="date"
            className="border rounded-md p-2"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
          <input
            type="date"
            className="border rounded-md p-2"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
          <button 
            className="bg-blue-500 text-white px-3 py-2 rounded-md hover:bg-blue-600"
            onClick={handleCreatePO}
          >
            Create PO
          </button>
        </div>
        
        <input
          type="email"
          placeholder="Email to"
          className="border w-full rounded-md p-2 mb-4"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <div className="grid gap-4">
          {
  [...purchaseOrders]
    .sort((a, b) => {
      const numA = (a.po_number || "").toString().toLowerCase();
      const numB = (b.po_number || "").toString().toLowerCase();

      return numB.localeCompare(numA, undefined, { numeric: true, sensitivity: 'base' });
    }).map((po) => (
            <div 
              key={po._id} 
              className="border-2 border-green-500 rounded-md p-4 flex justify-between items-center cursor-pointer hover:bg-green-100 transition"
              onClick={() => handleDownloadPO(po._id)}
            >
              <div className="grid grid-cols-4 gap-4 w-full text-sm">
                <div>
                  <p><strong>PO #</strong></p>
                  <p>{po.po_number || "N/A"}</p>
                </div>
                <div>
                  <p><strong>PO Date</strong></p>
                  <p>{po.po_date || "N/A"}</p>
                </div>
                <div>
                  <p><strong>Balance Due</strong></p>
                  <p>${po.balance_due || "0.00"}</p>
                </div>
                <div>
                  <p><strong>Invoices</strong></p>
                  <p>{po.invoices?.length || "0"}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ManualStatementPage;
